/**
 * SentimentBadge - Bullish/Bearish/Neutral indicator pill
 *
 * Built on top of the Badge primitive for consistent styling.
 */
import { Badge, type BadgeIntent } from '../common/Badge'

export type Sentiment = 'bullish' | 'bearish' | 'neutral' | 'unknown'

interface SentimentBadgeProps {
  sentiment: Sentiment
  label?: string // Optional custom label
}

const SENTIMENT_INTENT: Record<Sentiment, BadgeIntent> = {
  bullish: 'success',
  bearish: 'danger',
  neutral: 'neutral',
  unknown: 'neutral',
}

const SENTIMENT_LABELS: Record<Sentiment, string> = {
  bullish: 'Bullish',
  bearish: 'Bearish',
  neutral: 'Neutral',
  unknown: 'Unknown',
}

export function SentimentBadge({ sentiment, label }: SentimentBadgeProps) {
  const intent = SENTIMENT_INTENT[sentiment] ?? 'neutral'
  const displayLabel = label ?? SENTIMENT_LABELS[sentiment] ?? 'Unknown'

  return (
    <Badge intent={intent} pill>
      {displayLabel}
    </Badge>
  )
}

/**
 * Convert a sentiment score (-1.0 to 1.0) to a Sentiment type
 */
export function scoreToSentiment(score: number | null | undefined): Sentiment {
  if (score === null || score === undefined) {
    return 'unknown'
  }
  if (score > 0.2) {
    return 'bullish'
  }
  if (score < -0.2) {
    return 'bearish'
  }
  return 'neutral'
}

/**
 * Convert management tone to Sentiment type
 */
export function toneToSentiment(
  tone: 'optimistic' | 'cautious' | 'neutral' | null | undefined
): Sentiment {
  if (!tone) {
    return 'unknown'
  }
  switch (tone) {
    case 'optimistic':
      return 'bullish'
    case 'cautious':
      return 'bearish'
    case 'neutral':
      return 'neutral'
    default:
      return 'unknown'
  }
}

export default SentimentBadge
